import { PERIOD_LIMIT, SKILLS, SKILL_KEYS, bucketize, type Bucket, type Period, type SessionPoint, type SkillKey } from "@mathtrain/engine";
import { useMemo, useState } from "react";
import { api } from "../lib/api";
import { useLoad } from "../lib/hooks";
import type { PublicUser, SessionsResponse } from "../lib/types";
import { Chart, type Series } from "./Chart";

const PERIODS: [Period, string][] = [["session", "Session"], ["day", "Day"], ["week", "Week"], ["month", "Month"]];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const md = (d: Date) => `${MON[d.getMonth()]} ${d.getDate()}`;
const monday = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));
function label(t: number, p: Period) {
  const d = new Date(t);
  if (p === "week") return md(monday(d));
  if (p === "month") return `${MON[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
  return md(d);
}
function longLabel(t: number, p: Period) {
  const d = new Date(t);
  if (p === "session") return `${md(d)}, ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  if (p === "week") return "Week of " + label(t, p);
  if (p === "month") return `${MON[d.getMonth()]} ${d.getFullYear()}`;
  return md(d);
}

/** Align one or two players' buckets on a shared axis. */
function align(a: Bucket[], b: Bucket[] | null, p: Period) {
  if (!b || p === "session") return { keys: a.map((x) => ({ key: x.key, t0: x.t0 })), A: a, B: null as (Bucket | undefined)[] | null };
  const all = new Map<string, number>();
  for (const x of [...a, ...b]) if (!all.has(x.key) || all.get(x.key)! > x.t0) all.set(x.key, x.t0);
  const keys = [...all].map(([key, t0]) => ({ key, t0 })).sort((x, y) => x.t0 - y.t0).slice(-PERIOD_LIMIT[p]);
  const ma = new Map(a.map((x) => [x.key, x])), mb = new Map(b.map((x) => [x.key, x]));
  return { keys, A: keys.map((k) => ma.get(k.key)), B: keys.map((k) => mb.get(k.key)) };
}

function Card({ title, sub, series, mine, fmt, lowerBetter, eps = 0.05, children }: {
  title: string; sub?: string; series: Series[]; mine: (number | null)[]; fmt(v: number): string; lowerBetter?: boolean; eps?: number; children: React.ReactNode;
}) {
  const vals = mine.map((v, i) => ({ v, i })).filter((x) => x.v != null) as { v: number; i: number }[];
  const last = vals[vals.length - 1], first = vals[0];
  const d = last && first && vals.length > 1 ? last.v - first.v : 0;
  const good = lowerBetter ? d < 0 : d > 0;
  return <div className="card">
    <div className="card-head">
      <span className="card-title">{title}</span>
      <span>{last && <b>{fmt(last.v)}</b>}{Math.abs(d) >= eps && <span className={"delta " + (good ? "up" : "dn")}>{d > 0 ? "+" : "−"}{fmt(Math.abs(d))}</span>}</span>
    </div>
    {sub && <div className="small mute">{sub}</div>}
    {series.length ? children : null}
  </div>;
}

function toCSV(sessions: SessionPoint[]): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const head = ["date", "time", "mode", "problems", "correct", "accuracy_pct", "time_vs_target_pct", "points", "avg_level",
    ...SKILL_KEYS.map((k) => "level_" + k), ...SKILL_KEYS.flatMap((k) => ["n_" + k, "correct_" + k])];
  const rows = sessions.map((s) => {
    const d = new Date(s.t), lv = s.lv ? Object.values(s.lv) as number[] : [];
    return [`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, `${pad(d.getHours())}:${pad(d.getMinutes())}`, s.mode,
      s.n, s.c, ((100 * s.c) / s.n).toFixed(1), ((100 * s.r) / s.n).toFixed(1), s.points,
      lv.length ? (lv.reduce((a, x) => a + x, 0) / lv.length).toFixed(2) : "",
      ...SKILL_KEYS.map((k) => s.lv?.[k] ?? ""), ...SKILL_KEYS.flatMap((k) => s.sk[k] ? [s.sk[k]![0], s.sk[k]![1]] : [0, 0])].join(",");
  });
  return [head.join(","), ...rows].join("\n") + "\n";
}

export function ProgressCharts({ username, isMe, defaultCompare = "" }: { username: string; isMe: boolean; defaultCompare?: string }) {
  const [period, setPeriod] = useState<Period>("day");
  const [skill, setSkill] = useState<SkillKey | "all">("all");
  const [vs, setVs] = useState(defaultCompare);
  const mine = useLoad(() => api<SessionsResponse>(`/users/${username}/sessions`), [username]);
  const users = useLoad(() => api<{ users: PublicUser[] }>("/users"), []);
  const other = useLoad(() => (vs ? api<SessionsResponse>(`/users/${vs}/sessions`) : Promise.resolve(null)), [vs]);
  const meUser = users.data?.users.find((u) => u.username.toLowerCase() === username.toLowerCase());
  const vsUser = users.data?.users.find((u) => u.username === vs);

  const view = useMemo(() => {
    if (!mine.data) return null;
    const a = bucketize(mine.data.sessions, period, skill);
    const b = other.data && period !== "session" ? bucketize(other.data.sessions, period, skill) : null;
    const { keys, A, B } = align(a, b, period);
    const col = (arr: (Bucket | undefined)[], f: (x: Bucket) => number | null) => arr.map((x) => (x ? f(x) : null));
    const mk = (f: (x: Bucket) => number | null): { series: Series[]; mine: (number | null)[] } => {
      const m = col(A, f);
      const s: Series[] = [{ name: meUser?.displayName ?? username, color: "var(--c-ink)", values: m }];
      if (B && vsUser) s.push({ name: vsUser.displayName, color: vsUser.color, values: col(B, f) });
      return { series: s, mine: m };
    };
    return {
      labels: keys.map((k) => label(k.t0, period)), long: keys.map((k) => longLabel(k.t0, period)), empty: !a.length,
      level: mk((x) => x.level), acc: mk((x) => x.acc), pace: mk((x) => x.pace), pts: mk((x) => x.points || null), n: mk((x) => x.n),
    };
  }, [mine.data, other.data, period, skill, meUser, vsUser, username]);

  const exportCsv = () => {
    const blob = new Blob([toCSV(mine.data!.sessions)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `mathtrain-${username}-sessions.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  if (mine.error) return <div className="error">{mine.error}</div>;
  if (!view) return <div className="empty">Loading…</div>;
  const sname = skill === "all" ? "all skills" : SKILLS[skill].name;
  const others = users.data?.users.filter((u) => u.username.toLowerCase() !== username.toLowerCase()) ?? [];

  return <div className="stack">
    <div className="seg" role="group" aria-label="Group by">
      {PERIODS.map(([k, v]) => <button key={k} aria-pressed={period === k} onClick={() => setPeriod(k)}>{v}</button>)}
    </div>
    <div className="chips" role="group" aria-label="Skill">
      {(["all", ...SKILL_KEYS] as const).map((k) => <button key={k} aria-pressed={skill === k} onClick={() => setSkill(k)}>{k === "all" ? "All skills" : SKILLS[k].name}</button>)}
    </div>
    {others.length > 0 && <div className="chips" role="group" aria-label="Compare with">
      <button aria-pressed={!vs} onClick={() => setVs("")}>No comparison</button>
      {others.map((u) => <button key={u.username} aria-pressed={vs === u.username} onClick={() => setVs(u.username)}>vs {u.displayName}</button>)}
    </div>}
    {vs && period === "session" && <div className="small mute">Comparisons show by day, week, or month.</div>}
    {view.empty ? <div className="card empty">No {skill === "all" ? "" : sname + " "}sessions yet.</div> : <>
      <Card title="Level" sub={skill === "all" ? "Average of all skills at the end of each period" : `${sname} at the end of each period`} fmt={(v) => v.toFixed(1)} {...view.level}>
        <Chart title="Level" labels={view.labels} long={view.long} series={view.level.series} fmt={(v) => v.toFixed(1)} pad={0.3} floor={1} ceil={10} />
      </Card>
      <Card title="Accuracy" sub="Hinted and skipped problems count as misses" fmt={(v) => Math.round(v) + "%"} eps={1} {...view.acc}>
        <Chart title="Accuracy" labels={view.labels} long={view.long} series={view.acc.series} fmt={(v) => Math.round(v) + "%"} min={0} max={100} />
      </Card>
      <Card title="Time vs target" sub="Average answer time as a share of each problem's target. Under 100% is faster than target." fmt={(v) => Math.round(v) + "%"} lowerBetter eps={1} {...view.pace}>
        <Chart title="Time vs target" labels={view.labels} long={view.long} series={view.pace.series} fmt={(v) => Math.round(v) + "%"} floor={0} pad={10} ref={100} />
      </Card>
      {skill === "all"
        ? <Card title="Points" fmt={(v) => String(Math.round(v))} eps={Infinity} series={view.pts.series.slice(0, 1)} mine={view.pts.mine}>
            <Chart title="Points" bars labels={view.labels} long={view.long} series={view.pts.series.slice(0, 1)} fmt={(v) => String(Math.round(v))} min={0} />
          </Card>
        : <Card title="Problems" fmt={(v) => String(Math.round(v))} eps={Infinity} series={view.n.series.slice(0, 1)} mine={view.n.mine}>
            <Chart title="Problems" bars labels={view.labels} long={view.long} series={view.n.series.slice(0, 1)} fmt={(v) => String(Math.round(v))} min={0} />
          </Card>}
    </>}
    {isMe && mine.data!.sessions.length > 0 && <button className="btn" onClick={exportCsv}>Export sessions as CSV<small>One row per session, for charting elsewhere</small></button>}
  </div>;
}
